export interface ServerSentEvent {
    /** The `event:` field; `message` when the block carries none. */
    event: string;
    /** The `data:` lines joined with newlines. */
    data: string;
}

interface Block {
    event: string;
    data: string[];
}

function newBlock(): Block {
    return { event: 'message', data: [] };
}

function readField(line: string, block: Block): void {
    if (line.startsWith(':')) {
        return;
    }
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
        value = value.slice(1);
    }
    if (field === 'event') {
        block.event = value;
    } else if (field === 'data') {
        block.data.push(value);
    }
}

/**
 * Reads a `text/event-stream` body as it arrives. Blocks are separated by a blank line and
 * may be split across chunks at any byte. Comment lines and fields other than `event` and
 * `data` are dropped, and a block without data yields nothing.
 */
export async function* readServerSentEvents(
    stream: ReadableStream<Uint8Array>
): AsyncGenerator<ServerSentEvent> {
    const decoder = new TextDecoder();
    const reader = stream.getReader();
    let buffered = '';
    let block = newBlock();

    const flush = (): ServerSentEvent | null => {
        const done = block.data.length > 0 ? { event: block.event, data: block.data.join('\n') } : null;
        block = newBlock();
        return done;
    };

    try {
        for (;;) {
            const { value, done } = await reader.read();
            buffered += done ? decoder.decode() : decoder.decode(value, { stream: true });

            let newline: number;
            while ((newline = buffered.search(/\r\n|\n|\r/)) >= 0) {
                const line = buffered.slice(0, newline);
                buffered = buffered.slice(newline + (buffered.startsWith('\r\n', newline) ? 2 : 1));
                if (line === '') {
                    const event = flush();
                    if (event) {
                        yield event;
                    }
                } else {
                    readField(line, block);
                }
            }

            if (done) {
                if (buffered !== '') {
                    readField(buffered, block);
                }
                const event = flush();
                if (event) {
                    yield event;
                }
                return;
            }
        }
    } finally {
        reader.releaseLock();
    }
}
